<!-- Bill Form -->
<div class="section bill-form">
  <!-- Two-column grid for bill fields -->
  <div class="bill-fields-grid">
    <!-- Left Column -->
    <div class="field-column">
      <!-- Single billing-period range field. Start/End still bind to the same
           form values, so the period validations all apply. -->
      <mat-form-field appearance="outline" class="bill-period-range"
        [class.field-error]="fieldErrors()['billingPeriodStart'] || fieldErrors()['billingPeriodEnd']">
        <mat-label>Billing Period<span class="required-marker">*</span></mat-label>
        <mat-date-range-input [rangePicker]="periodPicker">
          <input matStartDate #periodStartInput placeholder="Start"
                 [ngModel]="billingPeriodStartValue"
                 (ngModelChange)="handleDateChange('billingPeriodStart', $event)"
                 (blur)="onDateBlur('billingPeriodStart', periodStartInput.value)" />
          <input matEndDate #periodEndInput placeholder="End"
                 [ngModel]="billingPeriodEndValue"
                 (ngModelChange)="handleDateChange('billingPeriodEnd', $event)"
                 (blur)="onDateBlur('billingPeriodEnd', periodEndInput.value)" />
        </mat-date-range-input>
        <mat-datepicker-toggle matSuffix [for]="periodPicker"></mat-datepicker-toggle>
        @if (confidenceData['billingPeriod']) {
          <div matSuffix class="confidence-dot-wrapper"
               matTooltipClass="confidence-tooltip"
               [matTooltip]="'Start date confidence: ' + confidenceData['billingPeriodStart']?.percent + '%\nEnd date confidence: ' + confidenceData['billingPeriodEnd']?.percent + '%'">
            <span class="confidence-dot" [class.good]="confidenceData['billingPeriod'].good" [class.bad]="confidenceData['billingPeriod'].bad" [class.ugly]="confidenceData['billingPeriod'].ugly"></span>
          </div>
        }
        <mat-date-range-picker #periodPicker></mat-date-range-picker>
      </mat-form-field>

      <app-score-select
        label="Provider / Supplier"
        [required]="true"
        [searchable]="true"
        [options]="supplierOptions"
        [value]="supplierSelectValue"
        [hasError]="!!fieldErrors()['supplierId']"
        (valueChange)="handleSupplierChange($event)">
      </app-score-select>

      <!-- Energy Plan -->
      <app-score-select
        label="Energy Plan"
        [options]="energyPlanOptions"
        [value]="formData.energyPlanId ?? null"
        [disabled]="energyPlansLoading || isEnergyPlanDisabled"
        [loading]="energyPlansLoading"
        [required]="isEnergyPlanRequired"
        [hasError]="isEnergyPlanRequired && !formData.energyPlanId"
        (valueChange)="handleEnergyPlanChange($event)">
      </app-score-select>

      <!-- Create New Plan button -->
      @if (canCreatePlan) {
        <button class="create-plan-btn" (click)="openCreatePlanDialog()">
          <i class="material-symbols-rounded">add</i>
          Create New Plan
        </button>
      }
    </div>

    <!-- Right Column -->
    <div class="field-column">
      <div class="usage-row">
        <mat-form-field appearance="outline" class="usage-amount-field"
          [class.field-error]="fieldErrors()['usageAmount']">
          <mat-label>Usage Amount<span class="required-marker">*</span></mat-label>
          <input matInput #usageInput type="text" [ngModel]="formattedUsageAmount" (ngModelChange)="handleFormattedNumberChange('usageAmount', $event)" (blur)="onFormattedNumberBlur('usageAmount', usageInput)" />
          @if (confidenceData['usageAmount']) {
            <div matSuffix class="confidence-dot-wrapper" [matTooltip]="'Confidence Score ' + confidenceData['usageAmount'].percent + '%'">
              <span class="confidence-dot" [class.good]="confidenceData['usageAmount'].good" [class.bad]="confidenceData['usageAmount'].bad" [class.ugly]="confidenceData['usageAmount'].ugly"></span>
            </div>
          }
        </mat-form-field>
        <!-- Required on gas: the API has no fallback gas unit (electricity falls back to kWh) -->
        <mat-form-field appearance="outline" class="usage-unit-field"
          [class.field-error]="fieldErrors()['usageUnit']"
          [class.field-warn]="fieldWarnings()['usageUnit']">
          <mat-label>Unit@if (isGasMeter()) {<span class="required-marker">*</span>}</mat-label>
          <mat-select [ngModel]="formData.usageUnit" (ngModelChange)="handleUnitChange($event)">
            <mat-option value="">-- Select Unit --</mat-option>
            @for (unit of availableUnits; track unit.id) {
              <mat-option [value]="unit.unit">{{ unit.unit }}</mat-option>
            }
          </mat-select>
          @if (confidenceData['usageUnit']) {
            <div matSuffix class="confidence-dot-wrapper" [matTooltip]="'Confidence Score ' + confidenceData['usageUnit'].percent + '%'">
              <span class="confidence-dot" [class.good]="confidenceData['usageUnit'].good" [class.bad]="confidenceData['usageUnit'].bad" [class.ugly]="confidenceData['usageUnit'].ugly"></span>
            </div>
          }
        </mat-form-field>
      </div>

      <!-- Demand (kW) — electric only; gas meters have no demand. -->
      @if (!isGasMeter()) {
        <mat-form-field appearance="outline">
          <mat-label>Demand (kW)</mat-label>
          <input matInput #demandInput type="text" [ngModel]="formattedDemand" (ngModelChange)="handleFormattedNumberChange('demandKw', $event)" (blur)="onFormattedNumberBlur('demandKw', demandInput)" />
          @if (confidenceData['demandKw']) {
            <div matSuffix class="confidence-dot-wrapper" [matTooltip]="'Confidence Score ' + confidenceData['demandKw'].percent + '%'">
              <span class="confidence-dot" [class.good]="confidenceData['demandKw'].good" [class.bad]="confidenceData['demandKw'].bad" [class.ugly]="confidenceData['demandKw'].ugly"></span>
            </div>
          }
        </mat-form-field>
      }

      <!-- Interval Usages (TOU Breakdown) — electric only. -->
      @if (!isGasMeter()) {
      <div class="interval-usages" [class.expanded]="showIntervalUsages">
        <button class="interval-toggle" (click)="showIntervalUsages = !showIntervalUsages">
          <i class="material-symbols-rounded">{{ showIntervalUsages ? 'expand_less' : 'expand_more' }}</i>
          Interval Usages
        </button>
        @if (showIntervalUsages) {
          <div class="interval-grid">
            <div class="interval-header">
              <span class="interval-hdr-label"></span>
              <span class="interval-hdr">Usage</span>
              <span class="interval-hdr">Cost</span>
              <span class="interval-hdr interval-hdr-rate">Rate</span>
            </div>
            <!-- On-Peak -->
            <div class="interval-row">
              <span class="interval-label">On-Peak</span>
              <div class="interval-input">
                <input #onPeakUsageInput type="text" [ngModel]="formattedOnPeakUsage"
                  (ngModelChange)="handleFormattedNumberChange('onPeakUsage', $event)"
                  (blur)="onFormattedNumberBlur('onPeakUsage', onPeakUsageInput)" />
              </div>
              <div class="interval-input">
                <span class="input-prefix">$</span>
                <input #onPeakCostInput type="text" [ngModel]="formattedOnPeakCost"
                  (ngModelChange)="handleCurrencyChange('onPeakCost', $event)"
                  (blur)="onCurrencyBlur('onPeakCost', onPeakCostInput)" />
              </div>
              <span class="interval-rate">{{ formattedOnPeakRate }}</span>
            </div>
            <!-- Intermediate -->
            <div class="interval-row">
              <span class="interval-label">Int-Peak</span>
              <div class="interval-input">
                <input #intPeakUsageInput type="text" [ngModel]="formattedIntPeakUsage"
                  (ngModelChange)="handleFormattedNumberChange('intermediateUsage', $event)"
                  (blur)="onFormattedNumberBlur('intermediateUsage', intPeakUsageInput)" />
              </div>
              <div class="interval-input">
                <span class="input-prefix">$</span>
                <input #intPeakCostInput type="text" [ngModel]="formattedIntPeakCost"
                  (ngModelChange)="handleCurrencyChange('intermediateCost', $event)"
                  (blur)="onCurrencyBlur('intermediateCost', intPeakCostInput)" />
              </div>
              <span class="interval-rate">{{ formattedIntPeakRate }}</span>
            </div>
            <!-- Off-Peak -->
            <div class="interval-row">
              <span class="interval-label">Off-Peak</span>
              <div class="interval-input">
                <input #offPeakUsageInput type="text" [ngModel]="formattedOffPeakUsage"
                  (ngModelChange)="handleFormattedNumberChange('offPeakUsage', $event)"
                  (blur)="onFormattedNumberBlur('offPeakUsage', offPeakUsageInput)" />
              </div>
              <div class="interval-input">
                <span class="input-prefix">$</span>
                <input #offPeakCostInput type="text" [ngModel]="formattedOffPeakCost"
                  (ngModelChange)="handleCurrencyChange('offPeakCost', $event)"
                  (blur)="onCurrencyBlur('offPeakCost', offPeakCostInput)" />
              </div>
              <span class="interval-rate">{{ formattedOffPeakRate }}</span>
            </div>
            <!-- Total -->
            <div class="interval-row interval-total-row">
              <span class="interval-label">Total</span>
              <span class="interval-total-cell" [class.mismatch]="intervalUsageMismatch">
                {{ formattedIntervalTotalUsage }}
                @if (intervalUsageMismatch) {
                  <i class="material-symbols-rounded interval-mismatch-icon"
                    [attr.aria-label]="intervalUsageMismatchTooltip"
                    [matTooltip]="intervalUsageMismatchTooltip"
                    matTooltipClass="bill-warning-tooltip">warning</i>
                }
              </span>
              <span class="interval-total-cell">
                <span class="input-prefix">$</span>{{ formattedIntervalTotalCost }}
              </span>
              <span class="interval-rate">
                {{ formattedIntervalBlendedRate }}</span>
            </div>
          </div>
        }
      </div>
      }
    </div>
  </div>

  <!-- Financials -->
  <div class="financials-header">
    <h4 class="section-label">
      <i class="material-symbols-rounded">attach_money</i>
      Financials
    </h4>
  </div>

  <!-- Basic Mode: Supply / Distribution breakdown -->
  @if (!isBudgetingMode()) {
    <div class="basic-financials">
      <!-- Distribution -->
      <div class="financial-group" [class.section-disabled]="!distributionEnabled()">
        <div class="financial-group-header">
          <i class="material-symbols-rounded dist-icon">speed</i>
          <span class="financial-group-title">Distribution</span>
          @if (sectionTogglesAvailable()) {
            <mat-slide-toggle class="section-toggle"
              [checked]="distributionEnabled()"
              (change)="setDistributionEnabled($event.checked)"
              [matTooltip]="distributionToggleTooltip"
              matTooltipClass="bill-warning-tooltip"></mat-slide-toggle>
          }
        </div>
        <div class="financial-line editable" [class.field-error]="fieldErrors()['distributionCharges']">
          <span class="financial-line-label">Charges@if (distributionEnabled()) {<span class="required-marker">*</span>}</span>
          <div class="financial-line-input">
            <span class="input-prefix">$</span>
            <input #distChargesInput type="text" [disabled]="!distributionEnabled()" [ngModel]="formattedDistCharges" (ngModelChange)="handleBasicCurrencyChange('distributionCharges', $event)" (blur)="onBasicCurrencyBlur('distributionCharges', distChargesInput)" />
          </div>
        </div>
        <div class="financial-line">
          <span class="financial-line-label">Effective Rate</span>
          <span class="financial-line-value">{{ formattedDistEffectiveRate }}</span>
        </div>
        <div class="financial-line editable">
          <span class="financial-line-label">Sales Tax</span>
          <div class="financial-line-input">
            <span class="input-prefix">$</span>
            <input #distTaxInput type="text" [disabled]="!distributionEnabled()" [ngModel]="formattedDistTax" (ngModelChange)="handleBasicCurrencyChange('distributionTaxes', $event)" (blur)="onBasicCurrencyBlur('distributionTaxes', distTaxInput)" />
          </div>
        </div>
        <div class="financial-line total">
          <span class="financial-line-label">Total Distribution</span>
          <span class="financial-line-value">${{ formattedTotalDist }}</span>
        </div>
      </div>

      <!-- Supply -->
      <div class="financial-group" [class.section-disabled]="!supplyEnabled()">
        <div class="financial-group-header">
          <i class="material-symbols-rounded supply-icon">lightbulb</i>
          <span class="financial-group-title">Supply</span>
          @if (sectionTogglesAvailable()) {
            <mat-slide-toggle class="section-toggle"
              [checked]="supplyEnabled()"
              (change)="setSupplyEnabled($event.checked)"
              [matTooltip]="supplyToggleTooltip"
              matTooltipClass="bill-warning-tooltip"></mat-slide-toggle>
          }
        </div>
        <div class="financial-line editable" [class.field-error]="fieldErrors()['supplyCharges']"
          [class.field-warn]="fieldWarnings()['supplyCharges']">
          <span class="financial-line-label">Charges@if (supplyEnabled()) {<span class="required-marker">*</span>}</span>
          <div class="financial-line-input">
            <span class="input-prefix">$</span>
            <input #supplyChargesInput type="text" [disabled]="!supplyEnabled()" [ngModel]="formattedSupplyCharges" (ngModelChange)="handleBasicCurrencyChange('supplyCharges', $event)" (blur)="onBasicCurrencyBlur('supplyCharges', supplyChargesInput)" />
          </div>
        </div>
        <div class="financial-line">
          <span class="financial-line-label">Effective Rate</span>
          <span class="financial-line-value">{{ formattedSupplyEffectiveRate }}</span>
        </div>
        <div class="financial-line editable">
          <span class="financial-line-label">Sales Tax</span>
          <div class="financial-line-input">
            <span class="input-prefix">$</span>
            <input #supplyTaxInput type="text" [disabled]="!supplyEnabled()" [ngModel]="formattedSupplyTax" (ngModelChange)="handleBasicCurrencyChange('supplyTaxes', $event)" (blur)="onBasicCurrencyBlur('supplyTaxes', supplyTaxInput)" />
          </div>
        </div>
        <div class="financial-line total">
          <span class="financial-line-label">Total Supply</span>
          <span class="financial-line-value">${{ formattedTotalSupply }}</span>
        </div>
      </div>
    </div>

    <!-- Total Billing Period Amount -->
    <ng-container *ngTemplateOutlet="totalCardTemplate"></ng-container>

    <!-- Budgeting Upsell Banner — only when the org isn't already entitled
         (entitled orgs just flip the Budgeting Mode toggle, no upgrade needed). -->
    @if (!orgHasBudgetingFeature) {
      <div class="pro-mode-banner">
        <div class="banner-icon">
          <i class="material-symbols-rounded">lock</i>
        </div>
        <div class="banner-content">
          <h4>Unlock Detailed Billing Information</h4>
          <p>Pro Mode extracts detailed line-item information from each bill, a must-have for budgeting. Contact your account manager to upgrade.</p>
        </div>
      </div>
    }
  }

  <!-- Pro Mode: Charge Sections Grid -->
  @if (isBudgetingMode()) {
    <div class="charge-sections-grid">
      <!-- Distribution Breakdown -->
      <div class="charge-details-section" [class.section-disabled]="!distributionEnabled()">
        <div class="section-title">
          <i class="material-symbols-rounded distribution">speed</i>
          <h4 class="enabled">Distribution Details</h4>
          @if (sectionTogglesAvailable()) {
            <mat-slide-toggle class="section-toggle"
              [checked]="distributionEnabled()"
              (change)="setDistributionEnabled($event.checked)"
              [matTooltip]="distributionToggleTooltip"
              matTooltipClass="bill-warning-tooltip"></mat-slide-toggle>
          }
        </div>
        <div class="charge-list distribution">
          @for (row of distributionChargeRows(); track row.field) {
            <app-charge-group-row [label]="row.label" [field]="row.field" [isGas]="isGasMeter()" [group]="chargeGroupMap[row.field]" [disabled]="!distributionEnabled()" (groupChange)="updateGroup(row.field, $event)" (moveItem)="handleMoveLineItem(row.field, $event.index, $event.target)"></app-charge-group-row>
          }
          <!-- Side summary, pinned to the bottom so both sides' totals line up. The pre-tax
               charges (same figure as basic mode) and, right under them, the effective rate
               with its calculation (charges ÷ usage). Sales tax follows because it is not
               part of the rate; the side total closes the column. -->
          <div class="side-summary dist">
            <div class="rate-block">
              <div class="financial-line subtotal">
                <span class="financial-line-label">Distribution Charges@if (distributionEnabled()) {<span class="required-marker">*</span>}</span>
                <span class="financial-line-value">${{ formattedDistCharges }}</span>
              </div>
              <div class="financial-line rate">
                <span class="financial-line-label"><i class="material-symbols-rounded">speed</i>Dist. Effective Rate</span>
                <span class="rate-calc">
                  @if (formattedDistRateFormula) {<span class="formula">{{ formattedDistRateFormula }}</span>}
                  <span class="financial-line-value">{{ formattedDistEffectiveRate }}</span>
                </span>
              </div>
            </div>
            <app-charge-group-row label="Distribution Sales Tax" field="distributionTaxCharges" [isGas]="isGasMeter()" [group]="chargeGroupMap['distributionTaxCharges']" [disabled]="!distributionEnabled()" (groupChange)="updateGroup('distributionTaxCharges', $event)" (moveItem)="handleMoveLineItem('distributionTaxCharges', $event.index, $event.target)"></app-charge-group-row>
            <div class="financial-line total">
              <span class="financial-line-label">Total Distribution</span>
              <span class="financial-line-value">${{ formattedTotalDist }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Supply Breakdown -->
      <div class="charge-details-section" [class.section-disabled]="!supplyEnabled()">
        <div class="section-title">
          <i class="material-symbols-rounded supply">lightbulb</i>
          <h4 class="enabled">Supply Details</h4>
          @if (sectionTogglesAvailable()) {
            <mat-slide-toggle class="section-toggle"
              [checked]="supplyEnabled()"
              (change)="setSupplyEnabled($event.checked)"
              [matTooltip]="supplyToggleTooltip"
              matTooltipClass="bill-warning-tooltip"></mat-slide-toggle>
          }
        </div>
        <div class="charge-list supply">
          @for (row of supplyChargeRows(); track row.field) {
            <app-charge-group-row [label]="row.label" [field]="row.field" [isGas]="isGasMeter()" [group]="chargeGroupMap[row.field]" [disabled]="!supplyEnabled()" (groupChange)="updateGroup(row.field, $event)" (moveItem)="handleMoveLineItem(row.field, $event.index, $event.target)"></app-charge-group-row>
          }
          <div class="side-summary supply">
            <div class="rate-block">
              <div class="financial-line subtotal">
                <span class="financial-line-label">Supply Charges@if (supplyEnabled()) {<span class="required-marker">*</span>}</span>
                <span class="financial-line-value">${{ formattedSupplyCharges }}</span>
              </div>
              <div class="financial-line rate">
                <span class="financial-line-label"><i class="material-symbols-rounded">lightbulb</i>Supply Effective Rate</span>
                <span class="rate-calc">
                  @if (formattedSupplyRateFormula) {<span class="formula">{{ formattedSupplyRateFormula }}</span>}
                  <span class="financial-line-value">{{ formattedSupplyEffectiveRate }}</span>
                </span>
              </div>
            </div>
            <!-- Sales tax on the supply side is a gas thing; an electricity bill only shows it
                 when the extraction or the user actually put something there. -->
            @if (showSupplySalesTax) {
              <app-charge-group-row label="Supply Sales Tax" field="supplySalesTaxes" [isGas]="isGasMeter()" [group]="chargeGroupMap['supplySalesTaxes']" [disabled]="!supplyEnabled()" (groupChange)="updateGroup('supplySalesTaxes', $event)" (moveItem)="handleMoveLineItem('supplySalesTaxes', $event.index, $event.target)"></app-charge-group-row>
            }
            <div class="financial-line total">
              <span class="financial-line-label">Total Supply</span>
              <span class="financial-line-value">${{ formattedTotalSupply }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Grand Total (Pro Mode) -->
    <ng-container *ngTemplateOutlet="totalCardTemplate"></ng-container>
  }
</div>

<ng-template #totalCardTemplate>
  <div class="total-card" [class.has-mismatch]="totalMismatch" [class.field-error]="fieldErrors()['totalAmount']"
    [class.field-warn]="fieldWarnings()['totalAmount']">
    <div class="total-main">
      <div class="total-label-section">
        <label>Total Billing Period Amount<span class="required-marker">*</span></label>
        @if (!totalManuallyChanged && confidenceData['totalAmount']) {
          <div class="confidence-dot-wrapper" [matTooltip]="'Confidence Score ' + confidenceData['totalAmount'].percent + '%'">
            <span class="confidence-dot" [class.good]="confidenceData['totalAmount'].good" [class.bad]="confidenceData['totalAmount'].bad" [class.ugly]="confidenceData['totalAmount'].ugly"></span>
          </div>
        }
        @if (totalIssue.hasIssue) {
          <i class="material-symbols-rounded total-issue-icon"
            [class.invalid]="totalIssue.iconGlyph === 'error'"
            [attr.aria-label]="totalIssue.tooltip"
            [matTooltip]="totalIssue.tooltip"
            matTooltipClass="bill-warning-tooltip">{{ totalIssue.iconGlyph }}</i>
        }
      </div>
      <div class="total-input-actions">
        <div class="total-input-wrapper total-editable">
          <span class="currency-symbol">$</span>
          <input #totalAmountInput type="text" [ngModel]="formattedTotalAmount" (ngModelChange)="handleCurrencyChange('totalAmount', $event)" (blur)="onCurrencyBlur('totalAmount', totalAmountInput)" />
        </div>
        @if (totalManuallyChanged && originalBillData) {
          <button class="revert-total-btn" (click)="revertTotalToExtracted()" title="Revert to extracted value (${{ formattedOriginalTotal }})">
            <i class="material-symbols-rounded">undo</i>
          </button>
        }
      </div>
    </div>
    <div class="total-effective-rate">
      <div class="label-group">
        <span class="label">Total Effective Rate</span>
        <span class="note">including taxes</span>
      </div>
      <span class="value">{{ formattedTotalEffectiveRate }}</span>
    </div>
    @if (totalIssue.explanation) {
      <div class="mismatch-detail">
        <div class="mc-header">
          <img src="/assets/img/jen/small/jen_zen_default.svg" alt="JenZen" class="mc-jen" />
          <div class="mc-body">
            <span class="mc-eyebrow">Reconcile total</span>
            <p class="mc-prose">{{ totalIssue.explanation }}</p>
          </div>
        </div>
        <div class="mc-actions">
          <button type="button" class="mc-action mc-action--supply" (click)="applyDiffToSupply()">
            <i class="material-symbols-rounded color-icon electric icon-fill">lightbulb</i>
            <span class="mc-action-text">
              <span class="mc-action-label">{{ totalIssue.supplyActionLabel }}</span>
              <span class="mc-action-amount">{{ totalIssue.supplyActionAmount }}</span>
            </span>
          </button>
          <button type="button" class="mc-action mc-action--dist" (click)="applyDiffToDistribution()">
            <i class="material-symbols-rounded color-icon icon-fill">speed</i>
            <span class="mc-action-text">
              <span class="mc-action-label">{{ totalIssue.distributionActionLabel }}</span>
              <span class="mc-action-amount">{{ totalIssue.distributionActionAmount }}</span>
            </span>
          </button>
          <button type="button" class="mc-action mc-action--total" (click)="syncTotalToCalculated()">
            <i class="material-symbols-rounded color-icon icon-fill">calculate</i>
            <span class="mc-action-text">
              <span class="mc-action-label">{{ totalIssue.recalculateActionLabel }}</span>
              <span class="mc-action-amount">{{ totalIssue.recalculateActionAmount }}</span>
            </span>
          </button>
        </div>
      </div>
    }
  </div>
</ng-template>
